import { Component, Element, Host, h, Prop, State, Watch} from "@stencil/core";
import { SwiperOptions } from "swiper";
import { NewsArticleTeaserDto, NewsClient } from "../../../../api/APIClient";
import { setPage } from "../../../../utils/page";
import { coerceNumberArray } from '../../../../utils/coerce';
import { ItemLinkProps } from '../../../../utils/link';
import { C7ElementResizeObserver } from '../../../../utils/resize-observer';
import { DateRender } from '../../../utility/date/date';


/**
 * @type news
 * @group special
 */
@Component({
    tag: "c7-news-carousel-single-page",
    styleUrl: "news-carousel-single-page.scss",
    shadow: false
})

export class NewsCarouselSinglePage {
    @Prop({mutable: true}) page: number;

    /**
     * @group pagination
     */
    @Prop() pageSize: number = 4;

    /**
     * @group pagination
     */
    @Prop() loop: boolean;

    /**
     * @group pagination
     */
    @Prop() enablePrevNextButtons: boolean = false;

    /**
     * @group pagination
     */
    @Prop() disablePager: boolean = false;

    /**
     * @group label
     */
    @Prop() readMoreLabel: string = "weiterlesen";

    /**
     * @group label
     */
    @Prop() transition: "slide" | "fade" = "slide";


    /**
    * @group categories
    */
    @Prop({mutable: true}) categories: number[] | string = [];
    @Watch('categories')
    onCategoriesChange(value) {
        this.categories = coerceNumberArray(value);
    }

    /**
     * @group categories
     */
    @Prop({mutable: true}) excludeCategories: number[] | string = [];
    @Watch('excludeCategories')
    onExcludeCategoriesChange(value: number[] | string) {
      this.excludeCategories = coerceNumberArray(value);
    }

    /**
     * @group items
     */
    @Prop({mutable: true}) items: number[] | string = [];
    @Watch('items')
    onItemsChange(value: number[] | string) {
      this.items = coerceNumberArray(value);
    }

    /**
     * @group items
     */
    @Prop({mutable: true}) excludeItems: number[] | string = [];
    @Watch('excludeItems')
    onExcludeItemsChange(value: number[] | string) {
      this.excludeItems = coerceNumberArray(value);
    }

    /**
     * @group content
     */
    @Prop() disableCategory: boolean = false;

  /**
   * @group content
   */
  @Prop() enableDate: boolean = false;


  @Element() element: HTMLElement;

    private _slidesRef!: HTMLC7SlidesElement;

    private resizeObserver: C7ElementResizeObserver = new C7ElementResizeObserver( {
        ['size-xs']: 576,
        ['size-sm']: 768,
        ['size-md']: 992,
        ['size-lg']: 1200,
        ['size-xl']: 1500,
        ['size-xxl']: 10000
    });

    connectedCallback() {
    this.resizeObserver.observe(this.element);
    }

    disconnectedCallback() {
    this.resizeObserver.unobserve(this.element);
    }

    @State() articles: NewsArticleTeaserDto[] = [];

    async componentWillLoad() {
        this.categories = coerceNumberArray(this.categories);
        this.items = coerceNumberArray(this.items);
        this.excludeCategories = coerceNumberArray(this.excludeCategories);
        this.excludeItems = coerceNumberArray(this.excludeItems);

        if(!this.page) {
            await setPage(this);
        }

        const client = new NewsClient();
        const {items} = await client.getArticles(this.page, 1, this.pageSize, this.categories, this.items, this.excludeCategories, this.excludeItems);

        this.articles = items;

    }

    render(){
        const options: SwiperOptions = {
            loop: this.loop,
            effect: this.transition,
            fadeEffect: {
              crossFade: true
            },
        }

        const cssClassNames = {
            'c7-news': true,
            'c7-news-carousel-single-page': true
        }

        const prevBtn = this.enablePrevNextButtons && (
            <button class={{
            'c7-carousel-navigation': true,
            'c7-carousel-navigation-prev': true
            }} onClick={() => this._slidesRef.slidePrev()}>
                <ion-icon aria-hidden="true" name="chevron-back-outline" />
            </button>
        );

        const nextBtn = this.enablePrevNextButtons && (
            <button class={{
              'c7-carousel-navigation': true,
              'c7-carousel-navigation-next': true
            }} onClick={() => this._slidesRef.slideNext()}>
              <ion-icon aria-hidden="true" name="chevron-forward-outline" />
            </button>
        );

        const positionalRender = {
            ['center-left']: [],
            ['center-right']: []
        }

        if (this.enablePrevNextButtons) {
          positionalRender['center-left'].push(prevBtn);
          positionalRender['center-right'].push(nextBtn);
        }

        return (
            <Host class={cssClassNames}>
                <div class={{'c7-news-carousel-center': true}}>
                    <div class={{'c7-news-carousel-left': true}}>
                      {positionalRender['center-left'].map(el => el)}
                    </div>

                    <div class={{'c7-news-carousel-main': true}}>
                        <c7-slides options={options} pager={!this.disablePager} ref={(el) => this._slidesRef = el}>
                    {
                      this.articles.map( article => {
                        const {0: category} = article.categoryNames;
                        return (
                          <c7-slide>
                            <article part="article">
                              <a {...ItemLinkProps(article)}  class={"c7-news-teaser-picture"}>
                                <c7-img src={article.teaserPictureUrl}/>
                              </a>

                              <div class={"c7-news-teaser-content"}>

                                {!this.disableCategory && (
                                  <div class={"c7-news-teaser-category"}>{category.name}</div>
                                )}
                                {this.enableDate && (
                                  <DateRender date={article.created} format={{
                                    day: '2-digit',
                                    month: '2-digit',
                                    year: 'numeric'
                                  }}/>
                                )}

                                <h3 class={"c7-news-teaser-title"}>{article.name}</h3>
                                <p class={"c7-news-teaser-text"}>{article.teaserText}</p>
                                <c7-button {...ItemLinkProps(article)}>
                                  {this.readMoreLabel}
                                </c7-button>

                              </div>
                            </article>
                          </c7-slide>
                        )
                      })
                    }
                    </c7-slides>
                    </div>

                    <div class={{'c7-news-carousel-right': true}}>
                      {positionalRender['center-right'].map(el => el)}
                    </div>
                </div>
            </Host>
        )
    }
}
